// V9 — Ultra-hyperlocal SEO push for the 3 priority cities.
// 5 top localities × 3 cities = 15 dedicated "Home Tutor in <Locality>, <City>"
// authority pages. Targets "home tutor in <neighborhood>" long-tail searches
// where competition is structurally lower than city-wide keywords.
//
// Routes follow the pattern /home-tutor-<locality-slug>-<city> so the existing
// categorizePath() rule (slug.endsWith("-<city>")) auto-tags every page with
// page_category = jabalpur | bhopal | nagpur, flowing into the Conversion
// Insights priority-city block with zero analytics changes.

const FOUNDED = "2013";
const RATING = "4.8";
const REVIEWS = "1,200+";

// Per-city top 5 localities + city metadata
const CITY_HYPERLOCAL = {
 jabalpur: {
 name: "Jabalpur", state: "Madhya Pradesh", board: "MP Board (MPBSE)",
 phone: "our Jabalpur Head Office team", phoneRaw: "our Jabalpur Head Office team",
 head_office: "Ghadi Chowk, Vijay Nagar, Jabalpur, Raksha 482002",
 geo: [23.1815, 79.9864],
 years: "12+",
 localities: [
 { name: "Vijay Nagar", slug: "vijay-nagar", postal: "482002", landmarks: "Ghadi Chowk, DPS Vijay Nagar, City Hospital", schools: "DPS, KV NCC, Maharishi Vidya Mandir" },
 { name: "Civil Lines", slug: "civil-lines", postal: "482001", landmarks: "High Court, Russell Chowk, Civil Lines Police Station", schools: "Christ Church, St. Aloysius" },
 { name: "Wright Town", slug: "wright-town", postal: "482002", landmarks: "Wright Town Stadium, Cantonment", schools: "St. Joseph's, Wright Town School" },
 { name: "Napier Town", slug: "napier-town", postal: "482001", landmarks: "Napier Town Market, Government Bus Stand", schools: "Govt. Boys/Girls Schools, Catholic schools" },
 { name: "Adhartal", slug: "adhartal", postal: "482004", landmarks: "Adhartal Lake, Madhotal Road", schools: "KV Adhartal, local CBSE/MP-Board schools" },
 ],
 },
 bhopal: {
 name: "Bhopal", state: "Madhya Pradesh", board: "MP Board (MPBSE)",
 phone: "our Jabalpur Head Office team", phoneRaw: "our Jabalpur Head Office team",
 head_office: "MP Nagar, Bhopal",
 geo: [23.2599, 77.4126],
 years: "12+",
 localities: [
 { name: "MP Nagar", slug: "mp-nagar", postal: "462011", landmarks: "Zone-1, Zone-2 markets, DB Mall, Habibganj station", schools: "DPS, Sagar Public School, MANIT" },
 { name: "Arera Colony", slug: "arera-colony", postal: "462016", landmarks: "10 Number Market, E-7, E-8 sectors", schools: "DPS Arera, Carmel Convent" },
 { name: "Shahpura", slug: "shahpura", postal: "462039", landmarks: "Shahpura Lake, FC Hill", schools: "Sagar Public Shahpura, Anand Vihar" },
 { name: "Kolar Road", slug: "kolar-road", postal: "462042", landmarks: "Manisha Market, Awadhpuri", schools: "Local CBSE / MP Board schools" },
 { name: "Habibganj", slug: "habibganj", postal: "462011", landmarks: "Habibganj Railway Station, MP Nagar adjacency", schools: "Schools clustered in Habibganj / MP Nagar belt" },
 ],
 },
 nagpur: {
 name: "Nagpur", state: "Maharashtra", board: "Maharashtra Board (MSBSHSE)",
 phone: "our Jabalpur Head Office team", phoneRaw: "our Jabalpur Head Office team",
 head_office: "Dharampeth, Nagpur",
 geo: [21.1458, 79.0882],
 years: "12+",
 localities: [
 { name: "Dharampeth", slug: "dharampeth", postal: "440010", landmarks: "Law College Square, Shankar Nagar", schools: "Centre Point School, Bhavan's" },
 { name: "Civil Lines", slug: "civil-lines", postal: "440001", landmarks: "Constitution Square, Vidhan Bhavan", schools: "St. Vincent Pallotti, Modern School" },
 { name: "Ramdaspeth", slug: "ramdaspeth", postal: "440010", landmarks: "Wardha Road junction, North Ambazari Road", schools: "Bhavan's BP Vidya Mandir, local CBSE schools" },
 { name: "Wardha Road", slug: "wardha-road", postal: "440015", landmarks: "AIIMS Nagpur, VNIT", schools: "VNIT-adjacent schools, KV CRPF" },
 { name: "Manish Nagar", slug: "manish-nagar", postal: "440015", landmarks: "Manish Nagar railway station, Somalwada", schools: "Local CBSE / Maharashtra Board schools" },
 ],
 },
};

const _stats = () => [
 { v: "1:1", l: "1 tutor : 1 student" },
 { v: "100%", l: "Background-verified tutors" },
 { v: "24-48h", l: "Demo scheduling window" },
 { v: `${RATING}/5`, l: `${REVIEWS} parent reviews` },
];

function _localBusinessSchema(city, locality) {
 return {
 "@context": "https://schema.org",
 "@type": "LocalBusiness",
 "@id": `https://anandtutorials.com/home-tutor-${locality.slug}-${city.name.toLowerCase()}#localbusiness`,
 "name": `Anand Tutorials ${city.name} — ${locality.name} home tutors`,
 "telephone": city.phone,
 "url": `https://anandtutorials.com/home-tutor-${locality.slug}-${city.name.toLowerCase()}`,
 "image": "https://anandtutorials.com/logo-anand-tutorials.png",
 "address": {
 "@type": "PostalAddress",
 "streetAddress": `${locality.name}, ${city.name}`,
 "addressLocality": city.name,
 "addressRegion": city.state,
 "postalCode": locality.postal,
 "addressCountry": "IN",
 },
 "geo": { "@type": "GeoCoordinates", "latitude": city.geo[0], "longitude": city.geo[1] },
 "openingHoursSpecification": [{
 "@type": "OpeningHoursSpecification",
 "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
 "opens": "07:00", "closes": "23:00",
 }],
 "areaServed": [{ "@type": "Place", "name": `${locality.name}, ${city.name}` }],
 "priceRange": "Fees are personalised based on class, subject, board, locality, tutor experience and number of classes. Talk to an Academic Advisor for a custom recommendation — your free demo is included.",
 "aggregateRating": { "@type": "AggregateRating", "ratingValue": RATING, "reviewCount": "1200", "bestRating": "5", "worstRating": "1" },
 };
}

function _buildPage(city, locality) {
 const slug = `home-tutor-${locality.slug}-${city.name.toLowerCase()}`;
 const otherLocalities = city.localities.filter((l) => l.slug !== locality.slug);
 return {
 path: `/${slug}`,
 eyebrow: `🏠 Hyperlocal · ${locality.name}, ${city.name}`,
 h1: `Home Tutor in ${locality.name}, ${city.name} — 1:1 Verified Tutors at Your Doorstep`,
 subTitle: `Anand Tutorials' verified 1:1 home tutors are active across ${locality.name} (PIN ${locality.postal}) and adjacent ${city.name} pin codes — covering ${locality.landmarks}. CBSE, ICSE, ${city.board}, IB, IGCSE. Class 1-12, NEET, JEE.`,
 seoTitle: `Home Tutor in ${locality.name}, ${city.name} — 1:1 Verified Tutors | Anand Tutorials`,
 seoDescription: `Best 1:1 home tutor in ${locality.name}, ${city.name} (PIN ${locality.postal}). Verified specialist tutors for CBSE, ICSE, ${city.board}, IB, IGCSE. ${RATING}/5 across ${REVIEWS} parent reviews. Free demo with no card.`,
 keywords: `home tutor ${locality.name.toLowerCase()} ${city.name.toLowerCase()}, home tuition ${locality.name.toLowerCase()}, home tutor in ${locality.name.toLowerCase()}, ${locality.name.toLowerCase()} home tutor ${city.name.toLowerCase()}, private tutor ${locality.name.toLowerCase()}, CBSE home tutor ${locality.name.toLowerCase()}, ICSE home tutor ${locality.name.toLowerCase()}, ${city.board.toLowerCase()} tutor ${locality.name.toLowerCase()}, tutor near ${locality.name.toLowerCase()}, ${locality.slug}-${city.name.toLowerCase()}`,
 focusKeyword: `home tutor ${locality.name.toLowerCase()} ${city.name.toLowerCase()}`,
 intent: `hyperlocal-${city.name.toLowerCase()}-${locality.slug}`,
 localBusinessSchema: _localBusinessSchema(city, locality),
 cityLocalities: city.localities.map((l) => ({ name: l.name, slug: l.slug })),
 stats: _stats(),
 pillars: [
 { t: `Active tutor deployment in ${locality.name}`, d: `Multiple verified home tutors live within a 3-5 km radius of ${locality.name} (${locality.landmarks}). Tutor travel is short, scheduling is flexible, demo class typically within 24-48 hours.` },
 { t: `Schools we already serve in ${locality.name}`, d: `Anand Tutorials tutors regularly support students from ${locality.schools} and other ${city.name} schools near ${locality.name}. Tutors know the local school exam pattern.` },
 { t: `${city.board}, CBSE & ICSE specialists`, d: `Subject-specialist matching by board. Maths specialist for Maths, Biology specialist for Biology — never a generalist on a paying ${locality.name} student.` },
 { t: "1:1 at home OR live online — your weekly choice", d: `Tutor visits your ${locality.name} home, OR joins via Zoom / Google Meet live two-way HD video. Switch week to week based on schedule.` },
 { t: "Background-verified — 4-step screening", d: "Academic credentials, subject test, teaching demo, police background check. We reject 8 of every 10 applicants." },
 { t: "Free demo with no card", d: `100% free 1:1 demo at your ${locality.name} home or live online. No advance fee, no card needed. Continue only if you're convinced.` },
 ],
 eeatTitle: `Why ${locality.name} parents pick Anand Tutorials`,
 eeat: [
 { pillar: "Experience", body: `${city.years} years of continuous 1:1 home tuition in ${city.name} since ${FOUNDED}. Active tutor deployment across ${locality.name} and 25+ other ${city.name} localities.` },
 { pillar: "Expertise", body: `${city.board}, CBSE and ICSE specialists matched per subject. Our tutors hold post-graduate qualifications from local institutions and have minimum 3 years of teaching the exact curriculum your child follows.` },
 { pillar: "Authoritativeness", body: `LocalBusiness + Service + Course + FAQ + Review schemas on every page. Published parent and tutor policies on /policies. Single Customer Care number ${city.phone} (Call / WhatsApp).` },
 { pillar: "Trustworthiness", body: `${RATING}/5 across ${REVIEWS} parent reviews · transparent process — fees are shared as a personalised quote after the free demo · first-month tutor-replacement window · refund-protected · no cash collection at the door — written into our Tutor Policy.` },
 ],
 coverage: [
 { tag: `${locality.name} · Class 1-5`, d: `Primary foundation home tuition — phonics, mental maths, handwriting, language fluency. Tutor visits your ${locality.name} home at a slot that fits the child's after-school window.` },
 { tag: `${locality.name} · Class 6-9`, d: `Algebra and Science foundation, language depth, NTSE / Olympiad layering. ${city.board}, CBSE and ICSE specialists.` },
 { tag: `${locality.name} · Class 10 Board`, d: `Chapter-weightage mocks, marking-scheme drilling, pre-board cycle from October to January.` },
 { tag: `${locality.name} · Class 11-12`, d: `PCM / PCB / Commerce / Arts stream specialists. Board + competitive overlap (JEE / NEET / CUET).` },
 { tag: `${locality.name} · NEET / JEE`, d: `1:1 AIIMS-alumni NEET mentors · IIT-alumni JEE mentors. ${locality.name} students get same-quality mentorship as Tier-1 metro students.` },
 { tag: `${locality.name} · IB / IGCSE`, d: `Cambridge-trained specialists for ${locality.name}'s international-curriculum families.` },
 ],
 compare: [
 ["Class format", "1:1 home tutor (or live online)", "60-100 student coaching batch"],
 ["Tutor verification", "4-step + police check", "Often unverified classifieds"],
 ["Travel for parent", `Tutor comes to ${locality.name}`, "Child commutes to coaching centre"],
 ["Same tutor entire term", "✔ Continuity guaranteed", "Rotating pool"],
 ["Sunday parent reports", "✔ WhatsApp written", "Monthly dashboard alert"],
 ["Free demo with no card", "✔", "Often paid mini-class"],
 ],
 faqs: [
 [`Are home tutors available in ${locality.name}?`,
 `Yes — multiple verified Anand Tutorials home tutors live near ${locality.name} (${locality.landmarks} area). Most ${locality.name} demos are scheduled within 24-48 hours via /contact or WhatsApp ${city.phone}.`],
 [`Which schools in ${locality.name} do you serve?`,
 `We regularly support students from ${locality.schools} and other ${city.name} schools near ${locality.name}. Tutors know the local school exam pattern and chapter weightage.`],
 [`How much does a home tutor in ${locality.name} cost?`,
 "Tuition fees depend on multiple factors such as class, subject, board, location, learning goals, and frequency of classes. Contact Anand Tutorials for a personalized recommendation and tutor matching — your free demo class is included."],
 [`Are female home tutors available in ${locality.name}?`,
 `Yes — verified female tutors are available across every subject and every grade in ${locality.name}. Police-verified, qualification-checked, demo-tested. Request during booking.`],
 [`Can the tutor visit my home in ${locality.name}?`,
 `Yes — tutor travels to your ${locality.name} home at the agreed time. Alternatively, you can switch any week to 1:1 live online if your child's school schedule changes.`],
 [`Will the same tutor teach my child entire term in ${locality.name}?`,
 "Yes — once the demo is approved, that tutor is yours for the full term. Replacement only if YOU request. No surprise faculty changes."],
 ],
 related: [
 { label: `Home Tuition in ${city.name}`, to: `/${city.name.toLowerCase()}` },
 { label: `1:1 Home Tuition ${city.name}`, to: `/1-to-1-home-tuition-${city.name.toLowerCase()}` },
 { label: `CBSE Home Tutor ${city.name}`, to: `/cbse-home-tutor-${city.name.toLowerCase()}` },
 { label: `ICSE Home Tutor ${city.name}`, to: `/icse-home-tutor-${city.name.toLowerCase()}` },
 { label: `NEET Home Tutor ${city.name}`, to: `/neet-home-tutor-${city.name.toLowerCase()}` },...otherLocalities.slice(0, 3).map((l) => ({
 label: `Home Tutor ${l.name}, ${city.name}`,
 to: `/home-tutor-${l.slug}-${city.name.toLowerCase()}`,
 })),
 { label: "Home Tutor Near Me", to: "/home-tutor-near-me" },
 { label: "Book a Free Demo", to: "/contact" },
 ],
 longFormSections: [
 { h2: `Why ${locality.name} parents are switching from coaching to 1:1 home tuition`,
 paragraphs: [
 `${locality.name} families historically defaulted to local coaching centres or generic home tutors found via classifieds. Both have structural ceilings. A coaching batch of 60-100 students gives your child under 60 seconds of direct interaction per 2-hour class. A classified-listing tutor is often unverified — qualifications unchecked, no demo accountability, no continuity guarantee. The result for many ${locality.name} children: hours invested, marks stagnant.`,
 `Anand Tutorials' 1:1 home tuition model is structurally different. The tutor is background-verified through our 4-step screening (academic credentials → subject test → teaching demo → police verification). Subject specialism is matched precisely — a ${city.board} Maths tutor, a CBSE Physics specialist, an ICSE Biology mentor. The first 1:1 demo is free, no card, no obligation. Parents may attend. You commit only after the demo confirms the right fit.`,
 ] },
 { h2: `Local ${locality.name} school knowledge — why it matters`,
 paragraphs: [
 `Our ${locality.name} tutors aren't generic graduates. They've taught students from ${locality.schools} and other ${city.name} schools near ${locality.name}. They know which chapters get higher weightage in the local school's internal exams, what answer-style the local examiners prefer, and the past 3-year trend of board-pattern questions for ${city.board} / CBSE / ICSE students.`,
 `That local fluency is the silent multiplier. A tutor who has prepared 50+ students from your child's school knows exactly where the syllabus hits hardest — and adapts the weekly study plan accordingly. From ${locality.landmarks} to the rest of ${city.name}, the same quality bar applies. Demo within 24-48 hours. Free first class. Tutor continues entire term.`,
 ] },
 ],
 };
}

// ---------------------------------------------------------------------------
// Build + export
// ---------------------------------------------------------------------------

const _pages = {};
for (const cityKey of Object.keys(CITY_HYPERLOCAL)) {
 const city = CITY_HYPERLOCAL[cityKey];
 for (const loc of city.localities) {
 const p = _buildPage(city, loc);
 _pages[p.path.slice(1)] = p;
 }
}

export const V9_HYPERLOCAL_PAGES = _pages;
export const V9_ALL_PATHS = Object.keys(V9_HYPERLOCAL_PAGES).map((s) => `/${s}`);
